import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Search, RefreshCw, Filter } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input, Label, Select } from '../../components/ui/Form';
import { useToast } from '../../contexts/ToastContext';
import { activitiesAPI } from '../../services/api';

const ActivitiesManagement = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [activities, setActivities] = useState([]);
  const [filters, setFilters] = useState({
    user_email: '',
    action: '',
    resource_type: '',
    start_date: '',
    end_date: '',
  });

  useEffect(() => {
    loadActivities();
  }, []);

  const loadActivities = async (overrideFilters = null) => {
    setLoading(true);
    try {
      const params = { ...(overrideFilters || filters) };
      const data = await activitiesAPI.getActivities(params);
      setActivities(Array.isArray(data) ? data : []);
    } catch (error) {
      toast.error('Failed to load activities', error.response?.data?.detail || 'Please try again');
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    setRefreshing(true);
    try {
      await loadActivities();
      toast.success('Activities refreshed');
    } catch (error) {
      toast.error('Failed to refresh activities');
    } finally {
      setRefreshing(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const applyFilters = async () => {
    await loadActivities(filters);
  };

  const clearFilters = async () => {
    const cleared = { user_email: '', action: '', resource_type: '', start_date: '', end_date: '' };
    setFilters(cleared);
    await loadActivities(cleared);
  };

  const formatTimestamp = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString();
  };

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Activities</h1>
            <p className="text-gray-600 mt-2">View system audit logs</p>
          </div>
          <div className="mt-4 sm:mt-0 flex items-center space-x-3">
            <Button variant="outline" size="sm" onClick={refreshData} disabled={refreshing}>
              <RefreshCw size={16} className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </motion.div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Filter size={18} className="mr-2" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <Label htmlFor="user_email">User Email</Label>
              <Input
                id="user_email"
                placeholder="Search by email"
                value={filters.user_email}
                onChange={(e) => handleFilterChange('user_email', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="action">Action</Label>
              <Select
                id="action"
                value={filters.action}
                onChange={(e) => handleFilterChange('action', e.target.value)}
              >
                <option value="">All</option>
                <option value="create">Create</option>
                <option value="update">Update</option>
                <option value="delete">Delete</option>
                <option value="login">Login</option>
                <option value="logout">Logout</option>
              </Select>
            </div>
            <div>
              <Label htmlFor="resource_type">Resource</Label>
              <Input
                id="resource_type"
                placeholder="e.g. user, school"
                value={filters.resource_type}
                onChange={(e) => handleFilterChange('resource_type', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="start_date">Start Date</Label>
              <Input
                id="start_date"
                type="date"
                value={filters.start_date}
                onChange={(e) => handleFilterChange('start_date', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="end_date">End Date</Label>
              <Input
                id="end_date"
                type="date"
                value={filters.end_date}
                onChange={(e) => handleFilterChange('end_date', e.target.value)}
              />
            </div>
          </div>
          <div className="mt-4 flex items-center space-x-3">
            <Button onClick={applyFilters}>
              <Search size={16} className="mr-2" />
              Apply
            </Button>
            <Button variant="outline" onClick={clearFilters}>
              Clear
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Activity Logs</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center text-gray-500 py-8">Loading activities...</div>
          ) : activities.length === 0 ? (
            <div className="text-center text-gray-500 py-8">No activities found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-600 border-b">
                    <th className="py-2 pr-4">Time</th>
                    <th className="py-2 pr-4">User</th>
                    <th className="py-2 pr-4">Action</th>
                    <th className="py-2 pr-4">Resource</th>
                    <th className="py-2 pr-4">Resource ID</th>
                  </tr>
                </thead>
                <tbody>
                  {activities.map((activity) => (
                    <tr key={activity.id} className="border-b last:border-b-0">
                      <td className="py-2 pr-4">{formatTimestamp(activity.timestamp)}</td>
                      <td className="py-2 pr-4">{activity.user_email}</td>
                      <td className="py-2 pr-4">{activity.action}</td>
                      <td className="py-2 pr-4">{activity.resource_type}</td>
                      <td className="py-2 pr-4">{activity.resource_id || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ActivitiesManagement;
